import { useState } from "react";
import { Cloud, CloudOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useLang } from "@/contexts/LangContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function ago(ts: number | null) {
  if (!ts) return null;
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  return `${m}m ago`;
}

/**
 * Global, always-available offline/sync indicator. Works the same on desktop,
 * laptop and mobile: hidden when everything is online and synced, expands into
 * a small panel with a manual "Sync now" when there is queued work.
 */
export function GlobalSyncStatus({ className }: { className?: string }) {
  const { t } = useLang();
  const { online, pendingCount, failedCount, syncing, syncProgress, lastSyncedAt, lastError, syncNow } =
    useOfflineSync();
  const [open, setOpen] = useState(false);

  if (online && pendingCount === 0 && !syncing) return null;

  const tone = !online
    ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
    : failedCount > 0
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : "border-primary/30 bg-primary/10 text-primary";

  return (
    <div className={cn("fixed bottom-20 right-4 z-[100] md:bottom-4", className)}>
      {open && (
        <div className="mb-2 w-72 rounded-2xl border bg-card/95 p-4 shadow-xl backdrop-blur">
          <p className="text-sm font-semibold">
            {online
              ? t("Syncing your changes", "আপনার পরিবর্তন সিঙ্ক হচ্ছে")
              : t("Working offline", "অফলাইনে কাজ করছে")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              `${pendingCount} change${pendingCount === 1 ? "" : "s"} saved on this device and will upload automatically.`,
              `${pendingCount}টি পরিবর্তন এই ডিভাইসে সংরক্ষিত — সংযোগ ফিরলে স্বয়ংক্রিয়ভাবে আপলোড হবে।`,
            )}
          </p>
          {syncing && (
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
          )}
          {lastError && (
            <p className="mt-2 line-clamp-2 text-xs text-destructive">{lastError}</p>
          )}
          {lastSyncedAt && (
            <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3 w-3" /> {t("Last synced", "সর্বশেষ সিঙ্ক")} {ago(lastSyncedAt)}
            </p>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="mt-3 w-full"
            disabled={!online || syncing}
            onClick={() => void syncNow()}
          >
            {t("Sync now", "এখনই সিঙ্ক করুন")}
          </Button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur transition",
          tone,
        )}
        aria-label={t("Sync status", "সিঙ্ক অবস্থা")}
      >
        {!online ? (
          <CloudOff className="h-4 w-4" />
        ) : syncing ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Cloud className="h-4 w-4" />
        )}
        <span>
          {!online
            ? pendingCount > 0
              ? t(`Offline · ${pendingCount} queued`, `অফলাইন · ${pendingCount} অপেক্ষমাণ`)
              : t("Offline", "অফলাইন")
            : syncing
              ? t("Syncing…", "সিঙ্ক হচ্ছে…")
              : t(`${pendingCount} to sync`, `${pendingCount} সিঙ্ক বাকি`)}
        </span>
      </button>
    </div>
  );
}

export default GlobalSyncStatus;
